import React, { useState } from 'react'
import { Form, Input, Button, InputNumber, Col, Row, Select } from 'antd'
import {
  landTypeOptions,
  propertyTypeOptions,
  getStoreysOptions,
  getExternalConstructionTypeOptions,
  bathroomOptions,
  bedroomOptions,
  rateData,
  bpiData,
} from '../data'

const getInitialData = () => {
  const landType = landTypeOptions[0].value
  const propertyType = propertyTypeOptions[0].value
  const storeys = getStoreysOptions(propertyType)[0].value
  const externalConstructionType = getExternalConstructionTypeOptions(propertyType)[0].value

  return {
    landType,
    propertyType,
    storeys,
    bedrooms: 2,
    bathrooms: 1,
    externalConstructionType,
  }
}

export const CalcForm = () => {
  const initialValues = getInitialData()
  const [form] = Form.useForm()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [result, setResult] = useState<[number, number]>()
  const [storeys, setStoreys] = useState<any[]>(getStoreysOptions(initialValues.propertyType))
  const [externalConstructionType, setExternalConstructionType] = useState<any[]>(
    getExternalConstructionTypeOptions(initialValues.propertyType),
  )

  const handleFinish = async (values: any) => {
    setIsSubmitting(true)

    const landType = String(values['landType'])
    const propertyType = String(values['propertyType'])
    const storeys = String(values['storeys'])
    const externalConstructionType = String(values['externalConstructionType'])
    const bedrooms = parseInt(values['bedrooms'])
    const bathrooms = parseInt(values['bathrooms'])
    let squareMetres = parseInt(values['squareMetres'])
    const postcode = parseInt(values['address']['postcode'])

    for (let i = 2; i < bedrooms; i++) {
      squareMetres += 16
    }

    const bpi = bpiData.find((o) => o.Postcode === postcode)
    const bpiModifier = bpi?.BPI

    const minRate = rateData.find((element) => {
      return (
        element['Land Type'] === landType &&
        element['Property Type'] === propertyType &&
        element.Storeys === storeys &&
        element['External Construction Type'] === externalConstructionType &&
        element['Quality of Finish'] === 'Low'
      )
    })

    if (minRate === undefined) throw new Error('Minimum Rate not found')

    const maxRate = rateData.find((element) => {
      return (
        element['Land Type'] === landType &&
        element['Property Type'] === propertyType &&
        element.Storeys === storeys &&
        element['External Construction Type'] === externalConstructionType &&
        element['Quality of Finish'] === 'High'
      )
    })

    if (maxRate === undefined) throw new Error('Maximum Rate not found')
    if (bpiModifier === undefined) throw new Error('BPI Modifier not found')

    let minCost = parseInt(minRate.Rate) * squareMetres * bpiModifier
    let maxCost = parseInt(maxRate.Rate) * squareMetres * bpiModifier

    for (let i = 1; i < bathrooms; i++) {
      minCost += 15000
      maxCost += 15000
    }

    setIsSubmitting(false)
    setResult([minCost, maxCost])
  }

  const handlePropertyTypeChange = (value: string) => {
    const newStoreys = getStoreysOptions(value)
    const newExternalConstructionType = getExternalConstructionTypeOptions(value)
    setStoreys(newStoreys)
    setExternalConstructionType(newExternalConstructionType)

    form.resetFields(['storeys'])
    form.resetFields(['externalConstructionType'])

    if (newStoreys.length) {
      form.setFieldValue('storeys', newStoreys[0])
    }
    if (newExternalConstructionType.length) {
      form.setFieldValue('externalConstructionType', newExternalConstructionType[0])
    }
  }

  return (
    <Form
      initialValues={initialValues}
      form={form}
      name="cost"
      requiredMark="optional"
      layout="vertical"
      autoComplete="off"
      onFinish={handleFinish}
      disabled={isSubmitting}
    >
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Land Type"
            name="landType"
            rules={[{ required: true, message: 'Land Type is required.' }]}
          >
            <Select options={landTypeOptions} />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item
            label="Property Type"
            name="propertyType"
            rules={[{ required: true, message: 'Property Type is required.' }]}
          >
            <Select options={propertyTypeOptions} onChange={handlePropertyTypeChange} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Storeys"
            name="storeys"
            rules={[{ required: true, message: 'Storeys is required.' }]}
          >
            <Select options={storeys} />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item
            label="External Construction Type:"
            name="externalConstructionType"
            rules={[{ required: true, message: 'External Construction Type is required.' }]}
          >
            <Select options={externalConstructionType} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col xs={12} sm={6}>
          <Form.Item
            label="Bedrooms:"
            name="bedrooms"
            rules={[{ required: true, message: 'Bedrooms is required.' }]}
          >
            <Select options={bedroomOptions} />
          </Form.Item>
        </Col>

        <Col xs={12} sm={6}>
          <Form.Item
            label="Bathrooms:"
            name="bathrooms"
            rules={[{ required: true, message: 'Bathrooms is required.' }]}
          >
            <Select options={bathroomOptions} />
          </Form.Item>
        </Col>

        <Col xs={12} sm={6}>
          <Form.Item
            label="Sq. Metres"
            name="squareMetres"
            rules={[{ required: true, message: 'Square Metres is required.' }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Col>

        <Col xs={12} sm={6}>
          <Form.Item
            label="Postcode"
            name={['address', 'postcode']}
            rules={[{ required: true, len: 4, message: 'Postcode is required.' }]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={isSubmitting}>
          Submit
        </Button>
      </Form.Item>

      {result && (
        <>
          <p>{`Minimum cost: ${result[0]}`}</p>
          <p>{`Maximum cost: ${result[1]}`}</p>
        </>
      )}
    </Form>
  )
}
